'use client'

import { useEffect, useMemo } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useMemberships } from './useMemberships'
import { useWorkspaceContext } from '@/store/workspaceContextStore'
import {
  decideWorkspaceRestore,
  holdsContextSync,
  isRestoringWorkspace,
  type WorkspaceSurface,
} from '@/lib/workspaceRouteRestore'

export interface WorkspaceRouteRestore {
  /** Keep showing the loader instead of painting the personal workspace. */
  restoring: boolean
  /** Don't mirror the URL into the workspace context yet (or at all). */
  holdContextSync: boolean
}

/**
 * Puts the remembered workspace back on the URL when a workspace surface is
 * entered without one (ENG-725) — the desktop app and the auth callback both
 * land on bare /projects, so without this the chip names your team while the
 * page shows personal content until you re-pick the team by hand.
 *
 * @param enabled false when the URL already names a workspace, or the surface
 *   is scoped some other way (library ghost mode).
 */
export function useWorkspaceRouteRestore(surface: WorkspaceSurface, enabled: boolean): WorkspaceRouteRestore {
  const router = useRouter()
  const search = useSearchParams().toString()
  const { memberships, isLoading: membershipsLoading } = useMemberships()
  const contextId = useWorkspaceContext(s => s.contextId)
  const hydrated = useWorkspaceContext(s => s.hydrated)

  // Hydrate here rather than leaning on the navbar's copy: this hook decides
  // the route, so it can't run before the persisted value has been applied.
  // Idempotent, and loses to any explicit setContext.
  useEffect(() => {
    useWorkspaceContext.getState().hydrate()
  }, [])

  const decision = useMemo(
    () =>
      decideWorkspaceRestore({
        enabled,
        hydrated,
        contextId,
        surface,
        teamOrgs: memberships?.teamOrgs ?? null,
        membershipsLoading,
        search,
      }),
    [enabled, hydrated, contextId, surface, memberships?.teamOrgs, membershipsLoading, search],
  )

  const navigateHref = decision.kind === 'navigate' ? decision.href : null
  useEffect(() => {
    if (!navigateHref) return
    // replace(): entering the app is not a history step you should be able to
    // go "back" from into the mismatched personal view.
    router.replace(navigateHref)
  }, [navigateHref, router])

  const shouldReset = decision.kind === 'reset'
  useEffect(() => {
    if (!shouldReset) return
    useWorkspaceContext.getState().setContext('personal')
  }, [shouldReset])

  return { restoring: isRestoringWorkspace(decision), holdContextSync: holdsContextSync(decision) }
}
